import {alpha, type Components, type Theme} from '@mui/material';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    bioplanTextSmall: true;
    paperTitle: true;
    monospaced: true;
    body3: true;
    description: true;
  }
}

declare module '@mui/material/styles/createTypography' {
  interface Typography {
    paperTitle: TypographyStyle;
    monospaced: TypographyStyle;
    body3: TypographyStyle;
    description: TypographyStyle;
  }
  interface TypographyOptions {
    paperTitle?: TypographyStyleOptions;
    monospaced?: TypographyStyleOptions;
    body3: TypographyStyleOptions;
    description: TypographyStyle;
  }
}

export const customMuiComponents: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        scrollbarWidth: 'thin',
        // scrollbarColor: theme.palette.action.hover,
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          backgroundColor: alpha(theme.palette.text.primary, 0.05),
          width: 8,
          height: 10,
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          borderRadius: 8,
          backgroundColor: theme.palette.action.selected,
          minHeight: 24,
        },
        '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
          backgroundColor: theme.palette.text.secondary,
        },
        '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
          backgroundColor: theme.palette.text.secondary,
        },
        '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
          backgroundColor: theme.palette.text.secondary,
        },
        '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
          backgroundColor: alpha(theme.palette.text.primary, 0.05),
        },
      },
    }),
  },
  MuiMenu: {
    defaultProps: {
      elevation: 1,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
    },
  },
  MuiModal: {
    defaultProps: {
      disableScrollLock: true,
      // marginThreshold: null,
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({theme}) => ({
        ':hover': {
          color: theme.palette.primary.main,
        },
        '&.MuiListItemIcon:hover': {
          color: theme.palette.primary.main,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.action.selected,
        },
        '&.Mui-selected:hover': {
          backgroundColor: alpha(theme.palette.action.selected, 0.1),
        },
        '&.Mui-selected:focus': {
          backgroundColor: alpha(theme.palette.action.selected, 0.1),
        },
      }),
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: ({theme}) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: 'inherit',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({theme}) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
  },
  MuiTooltip: {
    defaultProps: {
      arrow: true,
      disableInteractive: true,
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: ({theme}) => ({
        [theme.breakpoints.up('xs')]: {
          minHeight: '50px',
        },
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        cursor: 'default',
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover',
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({theme}) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiDialog: {
    defaultProps: {
      PaperProps: {
        elevation: 1,
      },
    },
  },
  MuiContainer: {
    defaultProps: {
      maxWidth: 'xl',
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        variants: [
          {
            props: {color: 'warning'},
            style: ({theme}) => ({
              color: theme.palette.warning.main,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.warning.main,
              },
              '& .MuiInputLabel-root,& .MuiFormHelperText-root': {
                color: theme.palette.warning.main,
              },
              '& .MuiFormHelperText-root': {
                color: theme.palette.warning.main,
              },
            }),
          },
        ],
      },
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: {
        fontWeight: 700,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        height: 2,
      },
    },
  },
};

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    good: Palette['primary'];
    bad: Palette['primary'];
    input: Palette['primary'];
    harvest: Palette['primary'];
    transfer: Palette['primary'];
    tableViewBackgroundColor: Palette['primary'];
    delousing: Palette['primary'];
    graph: Palette['primary'];
    error: Palette['primary'];
  }
  interface PaletteOptions {
    neutral: PaletteOptions['primary'];
    good: PaletteOptions['primary'];
    bad: PaletteOptions['primary'];
    input: PaletteOptions['primary'];
    harvest: PaletteOptions['primary'];
    transfer: PaletteOptions['primary'];
    tableViewBackgroundColor: PaletteOptions['primary'];
    delousing: PaletteOptions['primary'];
    graph: PaletteOptions['primary'];
  }
}

declare module '@mui/material/PaginationItem' {
  interface PaginationItemPropsColorOverrides {
    error: true;
  }
}

declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    good: true;
  }
}
